import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
  RadioGroup,
  Radio,
  FormControlLabel,
  Alert,
  Switch,
  IconButton,
  Tooltip
} from '@mui/material';
import { 
  Close as CloseIcon,
  Check as CheckIcon,
  Edit as EditIcon
} from '@mui/icons-material';
import { z } from 'zod';

import { I18nNamespace } from '@/i18n/types/i18nNamespace.ts';
import type { I18nScenarioEditorNs, I18nCommonNs } from '@/i18n/dictionaries/interfaces.ts';
import type { VariableSettingsDto, VariableValueSettingsDto } from '@/api-gen/api.schemas';
import useStyles from './styles';
import { Signal, useComputed, useSignal } from '@preact/signals-react';
import { useSelector } from 'react-redux';
import { currentUser } from '@/features/user';
import { isUserOwnerSelector, isUserSuperAdminSelector } from 'app/selectors/userSelectors.ts';
import { createTranslationField } from '@/app/utils/i18nUtils.ts';

/** 
 * Dialog for creating/editing a single variable (and its optional multiple values). 
 * It now uses react-hook-form from ScenarioVariablesSettings directly, rather than local state.
 */
interface VariableDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (variable: VariableSettingsDto, editingIndex?: number) => void;
  editingIndex?: number;
  initialVariable?: VariableSettingsDto;
  selectedCompanyId: Signal<number | null>;
  isShared: boolean;
}

const VariableDialog: React.FC<VariableDialogProps> = ({
  open,
  onClose,
  onSubmit,
  editingIndex,
  initialVariable,
  selectedCompanyId,
  isShared
}) => {
  const [translate] = useTranslation([I18nNamespace.ScenarioEditor]);
  const [translateCommon] = useTranslation([I18nNamespace.Common]);
  const classes = useStyles();

  // Role checks
  const isSuperAdmin = useSelector(isUserSuperAdminSelector);
  const isOwner = useSelector(isUserOwnerSelector);
  
  // Create type-safe translation field accessors
  const i18n = createTranslationField<I18nScenarioEditorNs>();
  const i18nCommon = createTranslationField<I18nCommonNs>();

  const variableName = useSignal('');
  const validationError = useSignal<string | null>(null); // Signal for validation error message
  const variableValues = useSignal<VariableValueSettingsDto[]>([]);
  const deletedValueIds = useSignal<number[]>([]);

  /**
   * Zod validation schema to ensure:
   * - A non-empty name with no whitespace
   * - At least one variable value
   */
  const validationSchema = React.useMemo(() => z.object({
    name: z
      .string()
      .min(1, { message: translate(i18n.variableLine.nameCantContainSpace) })
      .regex(/^\S+$/, { message: translate(i18n.variableLine.nameCantContainSpace) }),
    values: z
      .array(
        z.object({
          id: z.number().nullable().optional(),
          value: z.string().min(1, { message: translate(i18n.variableLine.noValues) }),
          companyId: z.number().nullable().optional()
        }) as z.ZodType<VariableValueSettingsDto>
      )
      .min(1, { message: translate(i18n.variableLine.noValues) })
  }), [i18n.variableLine.nameCantContainSpace, i18n.variableLine.noValues, translate]);
  const sharedValues = useComputed(() => 
    variableValues.value.filter(v => v.companyId == null)
  );
  const companyValues = useComputed(() => 
    variableValues.value.filter(v => v.companyId === selectedCompanyId.value)
  );
  
  // For inline editing
  const editingValueIndex = useSignal<number | null>(null);
  const editingValueText = useSignal('');
  // For backward compatibility with existing code
  const filteredVariableValues = useComputed(() => {
    if (varSource.value === 'useShared') {
      return sharedValues.value;
    } else if (varSource.value === 'useCompany') {
      return companyValues.value;
    }
    // Default case: return all values
    return variableValues.value;
  });

  const varSource = useSignal<'useShared' | 'useCompany'>('useShared');
  const useCompanySpecificVaues = useSignal(false);

  const newSharedValue = useSignal('');
  const newCompanyValue = useSignal('');
  const sharedInputFocused = useSignal(false);
  const companyInputFocused = useSignal(false);

  const hasCompanyValues = useComputed(() =>
    variableValues.value.some(v => v.companyId === selectedCompanyId.value)
  );

  const showCompanyValuesWarning = useComputed(() =>
    varSource.value === 'useShared' && hasCompanyValues.value
  );
  
  // Shared scenario rule: only superadmins and owners can edit shared values.
  // Admins/members can view shared values but not modify them.
  const disableSharedValues = isShared && !isSuperAdmin && !isOwner;

  // Initialize dialog state when opened
  React.useEffect(() => {
    if (open && initialVariable) {
      variableName.value = initialVariable.name || '';
      variableValues.value = initialVariable.values || [];
      deletedValueIds.value = [...(initialVariable.deletedValueIds ?? [])];

      // Set varSource based on whether variableValues contains any items with current user's company ID
      const hasCompanyValues = (initialVariable.values || []).some(
        v => v.companyId === selectedCompanyId.value
      );
      varSource.value = hasCompanyValues ? 'useCompany' : 'useShared';
    } else if (open && !initialVariable) {
      variableName.value = '';
      variableValues.value = [];
      deletedValueIds.value = [];
      varSource.value = 'useShared';
    }
    // Clear validation error when dialog opens or initialVariable changes
    validationError.value = null; 
  }, [open, initialVariable, selectedCompanyId.value]);

  const handleAddSharedValue = () => {
    if (newSharedValue.value.trim()) {
      const newValueObj = {
        id: 0,
        value: newSharedValue.value,
        companyId: null
      };

      // Update the variableValues signal with the new shared value
      variableValues.value = [...variableValues.value, newValueObj];
      validationError.value = null; // Clear error on adding value

      // Clear the input field
      newSharedValue.value = '';
    }
  };

  const handleAddCompanyValue = () => {
    if (newCompanyValue.value.trim() && selectedCompanyId.value !== null) {
      const newValueObj = {
        id: 0,
        value: newCompanyValue.value,
        companyId: selectedCompanyId.value
      };

      // Update the variableValues signal with the new company value
      variableValues.value = [...variableValues.value, newValueObj];
      validationError.value = null; // Clear error on adding value

      // Clear the input field
      newCompanyValue.value = '';
    }
  };

  const handleRemoveValue = (index: number) => {
    const removedValue = variableValues.value[index];
    const removedValueId = removedValue?.id ?? 0;
    if (removedValueId > 0 && !deletedValueIds.value.includes(removedValueId)) {
      deletedValueIds.value = [...deletedValueIds.value, removedValueId];
    }
    variableValues.value = variableValues.value.filter((_, i) => i !== index);
  };
  
  const handleStartEditing = (index: number, value: string) => {
    editingValueIndex.value = index;
    editingValueText.value = value;
  };
  
  const handleSaveEdit = () => {
    if (editingValueIndex.value !== null && editingValueText.value.trim()) {
      const newValues = [...variableValues.value];
      newValues[editingValueIndex.value] = {
        ...newValues[editingValueIndex.value],
        value: editingValueText.value.trim()
      };
      variableValues.value = newValues;
      cancelEditing();
    }
  };
  
  const cancelEditing = () => {
    editingValueIndex.value = null;
    editingValueText.value = '';
  };
  
  const handleEditKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSaveEdit();
    } else if (e.key === 'Escape') {
      cancelEditing();
    }
  };

  const handleSubmitClick = async () => {
    validationError.value = null;

    const validationResult = validationSchema.safeParse({
      name: variableName.value,
      values: variableValues.value
    });

    if (!validationResult.success) {
      validationError.value = validationResult.error.errors.map((error) => error.message).join(', ');
      return;
    }

    const updated: VariableSettingsDto = {
      id: initialVariable?.id ?? 0,
      name: variableName.value.trim(),
      values: variableValues.value,
      deletedValueIds: deletedValueIds.value
    };

    onSubmit(updated, editingIndex);
    onClose(); // Close only on successful submission
  };

  const handleSharedKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleAddSharedValue();
    }
  };

  const handleCompanyKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleAddCompanyValue();
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth={isShared ? 'md' : 'sm'} fullWidth PaperProps={{
      sx: { borderRadius: 2, boxShadow: '0 8px 24px rgba(0,0,0,0.12)' }
    }}>
      <DialogTitle sx={{
        borderBottom: '1px solid rgba(0, 0, 0, 0.08)',
        pb: 2,
        pt: 2.5,
        fontSize: '1.25rem',
        fontWeight: 600
      }}>
        {editingIndex !== undefined
          ? `${translateCommon(i18nCommon.buttonLabels.edit)} ${translate(i18n.variableLine.variable)}`
          : translate(i18n.addVariableDialog.addVariable)
        }
      </DialogTitle>
      <DialogContent sx={{ pt: 5, pb: 2 }}>
        <Box sx={{ mt: 1 }}>
          <TextField
            value={variableName.value}
            onChange={(e) => variableName.value = e.target.value}
            fullWidth
            variant="outlined"
            size="small"
            label={translate(i18n.addVariableDialog.variableName)}            
            error={variableName.value.includes(' ')}
            helperText={variableName.value.includes(' ') ? translate(i18n.variableLine.nameCantContainSpace) : ''}
            sx={{ mb: 3, mt: 2 }}
            InputLabelProps={{
              shrink: true,
            }}
          />
          
          {/* Validation Error Alert */}
          {validationError.value && (
            <Alert severity="error" sx={{ mb: 2, mt: 1 }}>
              {validationError.value}
            </Alert>
          )}

          <Box mb={2}>
            {disableSharedValues && (
              <Alert
                severity="info"
                sx={{
                  mb: 2,
                  fontSize: '0.875rem',
                  '& .MuiAlert-icon': {
                    alignItems: 'center'
                  }
                }}
              >
                {translate(i18n.variableLine.sharedValuesDisabled)}
              </Alert>
            )}
          </Box>

          <Box
            sx={{
              display: 'flex',
              gap: 2,
              width: '100%',
              mb: 2
            }}
          >
            {/* Shared values input section — visible for all; disabled for Admin on shared scenarios */}
            <Box sx={{ width: isShared ? '50%' : '100%' }}>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                {isShared ? translate(i18n.variableLine.sharedValues) : translate(i18n.variableLine.valuesLabel)}
              </Typography>
              <Box display="flex" flexDirection="column">
                <Box display="flex" alignItems="center">
                  <TextField
                    value={newSharedValue.value}
                    onChange={(e) => newSharedValue.value = e.target.value}
                    fullWidth
                    variant="outlined"
                    size="small"                    
                    onKeyPress={handleSharedKeyPress}
                    onFocus={() => sharedInputFocused.value = true}
                    onBlur={() => sharedInputFocused.value = false}
                    disabled={disableSharedValues}
                  />
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleAddSharedValue}
                    disabled={disableSharedValues}
                    sx={{
                      ml: 1,
                      minWidth: '85px',
                      height: '40px',
                      borderRadius: '4px',
                      boxShadow: 'none'
                    }}
                  >
                    {translateCommon(i18nCommon.buttonLabels.add)}
                  </Button>
                </Box>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{
                    mt: 0.5,
                    ml: 0.5,
                    height: '1.25rem',
                    opacity: sharedInputFocused.value && !disableSharedValues ? 1 : 0,
                    transition: 'opacity 0.2s ease-in-out'
                  }}
                >
                  {sharedInputFocused.value && !disableSharedValues ? translateCommon(i18nCommon.buttonLabels.hitEnterToAdd) : ''}
                </Typography>
              </Box>
            </Box>

            {isShared && (
              <>
                {/* Vertical divider */}
                <Box sx={{ 
                  width: '1px', 
                  backgroundColor: 'rgba(0, 0, 0, 0.12)', 
                  my: 1,
                  alignSelf: 'stretch'
                }} />

                {/* Company values input section */}
                <Box sx={{ width: '50%' }}>
                  <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                    {translate(i18n.variableLine.companyValues)}
                  </Typography>
                  <Box display="flex" flexDirection="column">
                    <Box display="flex" alignItems="center">
                      <TextField
                        value={newCompanyValue.value}
                        onChange={(e) => newCompanyValue.value = e.target.value}
                        fullWidth
                        variant="outlined"
                        size="small"
                        onKeyPress={handleCompanyKeyPress}
                        onFocus={() => companyInputFocused.value = true}
                        onBlur={() => companyInputFocused.value = false}
                      />
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={handleAddCompanyValue}
                        sx={{
                          ml: 1,
                          minWidth: '85px',
                          height: '40px',
                          borderRadius: '4px',
                          boxShadow: 'none'
                        }}
                      >
                        {translateCommon(i18nCommon.buttonLabels.add)}
                      </Button>
                    </Box>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{
                        mt: 0.5,
                        ml: 0.5,
                        height: '1.25rem',
                        opacity: companyInputFocused.value ? 1 : 0,
                        transition: 'opacity 0.2s ease-in-out'
                      }}
                    >
                      {companyInputFocused.value ? translateCommon(i18nCommon.buttonLabels.hitEnterToAdd) : ''}
                    </Typography>
                  </Box>
                </Box>
              </>
            )}
          </Box>

          <Box
            sx={{
              display: 'flex',
              gap: 2,
              width: '100%'
            }}
          >
            {/* Shared values section — visible for all; read-only for Admin on shared scenarios */}
            <Box sx={{ width: isShared ? '50%' : '100%' }}>
              {/* <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                {translate(i18n.variableLine.sharedValues)}
              </Typography> */}
              <Box
                sx={{
                  border: '1px solid rgba(0, 0, 0, 0.12)',
                  borderRadius: 1,
                  p: 1,
                  height: '200px',
                  overflowY: 'auto',
                  backgroundColor: sharedValues.value.length ? 'transparent' : 'rgba(0, 0, 0, 0.02)'
                }}
              >
                {sharedValues.value.length > 0 ? (
                  sharedValues.value.map((value, index) => {
                    const valueIndex = variableValues.value.findIndex(v => v.value === value.value && v.companyId == null);
                    return (
                      <Box
                        key={`shared-${index}`}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          p: 1,
                          mb: index < sharedValues.value.length - 1 ? 1 : 0,
                          borderRadius: 1,
                          bgcolor: disableSharedValues ? 'rgba(0, 0, 0, 0.05)' : 'rgba(0, 0, 0, 0.03)',
                          opacity: disableSharedValues ? 0.7 : 1
                        }}
                      >
                        {editingValueIndex.value === valueIndex ? (
                          <TextField
                            value={editingValueText.value}
                            onChange={(e) => editingValueText.value = e.target.value}
                            onKeyDown={handleEditKeyPress}
                            onBlur={handleSaveEdit}
                            variant="standard"
                            autoFocus
                            fullWidth
                            size="small"
                            sx={{ ml: 1, mr: 1 }}
                          />
                        ) : (
                          <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                            <Typography 
                              variant="body2" 
                              sx={{ 
                                fontWeight: 500, 
                                pl: 1,
                                flex: 1,
                              }}
                            >
                              {value.value}
                            </Typography>
                            {!disableSharedValues && (
                              <Tooltip title="Edit value">
                                <IconButton
                                  size="small"
                                  onClick={() => handleStartEditing(valueIndex, value.value)}
                                  sx={{ 
                                    opacity: 0.5,
                                    '&:hover': { opacity: 1 },
                                    mr: 1
                                  }}
                                >
                                  <EditIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                          </Box>
                        )}
                        <Box sx={{ display: 'flex' }}>
                          {editingValueIndex.value === valueIndex ? (
                            <>
                              <Tooltip title="Save changes">
                                <IconButton 
                                  onClick={handleSaveEdit} 
                                  size="small"
                                  sx={{ color: 'success.main' }}
                                >
                                  <CheckIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Cancel editing">
                                <IconButton 
                                  onClick={cancelEditing} 
                                  size="small"
                                  sx={{ color: 'error.main' }}
                                >
                                  <CloseIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </>
                          ) : (
                            <Tooltip title="Remove value">
                              <span>
                                <IconButton
                                  onClick={() => handleRemoveValue(valueIndex)}
                                  size="small"
                                  disabled={disableSharedValues}
                                  sx={{
                                    color: 'rgba(0, 0, 0, 0.54)',
                                    '&:hover': {
                                      color: 'error.main',
                                      bgcolor: 'rgba(211, 47, 47, 0.04)'
                                    }
                                  }}
                                >
                                  <CloseIcon fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                          )}
                        </Box>
                      </Box>
                    );
                  })
                ) : (
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                    <Typography variant="body2" color="text.secondary">
                      {isShared ? translate(i18n.variableLine.noSharedValues) : translate(i18n.variableLine.noValues)}
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>

            {isShared && (
              <>
                {/* Vertical divider */}
                <Box sx={{ 
                  width: '1px', 
                  backgroundColor: 'rgba(0, 0, 0, 0.12)', 
                  my: 1,
                  alignSelf: 'stretch'
                }} />

                {/* Company-specific values section */}
                <Box sx={{ width: '50%' }}>
                  {/* <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                    {translate(i18n.variableLine.companyValues)}
                  </Typography> */}
                  <Box
                    sx={{
                      border: '1px solid rgba(0, 0, 0, 0.12)',
                      borderRadius: 1,
                      p: 1,
                      height: '200px',
                      overflowY: 'auto',
                      backgroundColor: companyValues.value.length ? 'transparent' : 'rgba(0, 0, 0, 0.02)'
                    }}
                  >
                    {companyValues.value.length > 0 ? (
                      companyValues.value.map((value, index) => {
                        const valueIndex = variableValues.value.findIndex(v => v.value === value.value && v.companyId === selectedCompanyId.value);
                        return (
                          <Box
                            key={`company-${index}`}
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              p: 1,
                              mb: index < companyValues.value.length - 1 ? 1 : 0,
                              borderRadius: 1,
                              bgcolor: 'rgba(25, 118, 210, 0.08)'
                            }}
                          >
                            {editingValueIndex.value === valueIndex ? (
                              <TextField
                                value={editingValueText.value}
                                onChange={(e) => editingValueText.value = e.target.value}
                                onKeyDown={handleEditKeyPress}
                                onBlur={handleSaveEdit}
                                variant="standard"
                                autoFocus
                                fullWidth
                                size="small"
                                sx={{ ml: 1, mr: 1 }}
                              />
                            ) : (
                              <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                                <Typography 
                                  variant="body2" 
                                  sx={{ 
                                    fontWeight: 500, 
                                    pl: 1,
                                    flex: 1,
                                  }}
                                >
                                  {value.value}
                                </Typography>
                                <Tooltip title="Edit value">
                                  <IconButton
                                    size="small"
                                    onClick={() => handleStartEditing(valueIndex, value.value)}
                                    sx={{ 
                                      opacity: 0.5,
                                      '&:hover': { opacity: 1 },
                                      mr: 1
                                    }}
                                  >
                                    <EditIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </Box>
                            )}
                            <Box sx={{ display: 'flex' }}>
                              {editingValueIndex.value === valueIndex ? (
                                <>
                                  <Tooltip title="Save changes">
                                    <IconButton 
                                      onClick={handleSaveEdit} 
                                      size="small"
                                      sx={{ color: 'success.main' }}
                                    >
                                      <CheckIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip title="Cancel editing">
                                    <IconButton 
                                      onClick={cancelEditing} 
                                      size="small"
                                      sx={{ color: 'error.main' }}
                                    >
                                      <CloseIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                </>
                              ) : (
                                <Tooltip title="Remove value">
                                  <IconButton
                                    onClick={() => handleRemoveValue(valueIndex)}
                                    size="small"
                                    sx={{
                                      color: 'rgba(0, 0, 0, 0.54)',
                                      '&:hover': {
                                        color: 'error.main',
                                        bgcolor: 'rgba(211, 47, 47, 0.04)'
                                      }
                                    }}
                                  >
                                    <CloseIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              )}
                            </Box>
                          </Box>
                        );
                      })
                    ) : (
                      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                        <Typography variant="body2" color="text.secondary">
                          {translate(i18n.variableLine.noCompanyValues)}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </Box>
              </>
            )}
          </Box>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid rgba(0, 0, 0, 0.08)' }}>
        <Button
          onClick={onClose}
          sx={{
            color: 'text.secondary',
            fontWeight: 500
          }}
        >
          {translateCommon(i18nCommon.buttonLabels.cancel)}
        </Button>
        <Button
          onClick={handleSubmitClick}
          variant="contained"
          color="primary"
          disabled={!variableName.value.trim() || variableName.value.includes(' ')}
          sx={{
            px: 3,
            boxShadow: 'none',
            fontWeight: 500
          }}
        >
          {editingIndex !== undefined
            ? translate(i18n.variableLine.update)
            : translate(i18n.addVariableDialog.addVariable)
          }
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default VariableDialog;
